import type {
  CardChange,
  CardChoice,
  CardIslandState,
  CardResult,
  CardSetupRef,
  PaymentReason,
  PaymentResult,
  PaymentStatus,
} from "./card-island";
import type { BuyerWalletPayment, SavedCard } from "./wallet-api";

/**
 * The card island's wire: the contract (card-island.ts) as messages, for
 * the island on its own page (`/checkout/card`) and whoever hosts it — the
 * Lissen app's WebView, or a document on the checkout's origin that frames
 * it. Both ends read what arrives as unknown until proved otherwise:
 * `parseHostMessage` on the page, `parseIslandMessage` in the host. A
 * WebView carries strings only, so both read a JSON string too.
 *
 * `confirm` and `confirmed` carry an `id` the host chose, so an answer is
 * matched to its question; `submit` needs none, the page answering one at a
 * time.
 *
 * The nonce: the app loads the page with a random one in its URL fragment
 * (`#nonce=…`, `islandUrl`), and from then on every message either way
 * carries it. The fragment never leaves the page's own document — no
 * request, no referrer, no frame inside it sees it — so a message without
 * it did not come from the page the app loaded. That matters on Android,
 * where the WebView's bridge is reachable from every frame in the page
 * (Stripe's, the bank's), not just the page itself. A host that frames the
 * page from its own origin (no WebView bridge) passes none and checks none.
 */

export const ISLAND_CHANNEL = "lissen-card-island";
export const ISLAND_VERSION = 1;

/** The fragment parameter the page reads its nonce from. */
export const ISLAND_NONCE_PARAM = "nonce";
const NONCE = /^[A-Za-z0-9_-]{16,128}$/;

/** The page's URL with the nonce in its fragment. */
export function islandUrl(pageUrl: string, nonce: string) {
  const url = new URL(pageUrl);
  url.hash = `${ISLAND_NONCE_PARAM}=${encodeURIComponent(nonce)}`;
  return url.href;
}

/** The nonce in a page's fragment (`location.hash`), if it is one. */
export function islandNonceOf(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, "")).get(
    ISLAND_NONCE_PARAM,
  );
  return value && NONCE.test(value) ? value : null;
}

/** Host → island. `init` once (the setup cannot change after it), `update`
 *  for anything else, `submit` to confirm the typed card, `confirm` to
 *  confirm a payment the engine prepared (its bank check drawn on the
 *  page under `reason`). `Appearance` is the page's to read. */
export type HostMessage<Appearance = unknown> =
  | ({
      type: "init";
      setup: CardSetupRef;
      email: string;
      appearance?: Appearance;
    } & Partial<CardIslandState>)
  | ({ type: "update"; email?: string } & Partial<CardIslandState>)
  | { type: "submit"; email?: string }
  | {
      type: "confirm";
      id: string;
      payment: BuyerWalletPayment;
      reason: PaymentReason;
    };

/** Island → host. `height` is the page's content height in CSS pixels, for
 *  sizing the view it is in; `focus` is where the focused field sits in it.
 *  `bank` says the bank's page opened or closed on the island's page, so a
 *  host that folded the island away shows it again while the buyer answers.
 *  `result` answers a `submit`, `confirmed` the `confirm` of the same id. */
export type IslandMessage =
  | { type: "ready" }
  | { type: "height"; height: number }
  | { type: "bank"; open: boolean }
  | ({ type: "change" } & CardChange)
  | { type: "focus"; top: number; bottom: number }
  | ({ type: "choose" } & CardChoice)
  | { type: "result"; result: CardResult }
  | { type: "confirmed"; id: string; result: PaymentResult };

type Envelope<T> = T & {
  channel: typeof ISLAND_CHANNEL;
  version: number;
  nonce?: string;
};

/** A message on the wire: the channel, the version and, when the page was
 *  loaded with one, the nonce. */
export const islandEnvelope = <T extends HostMessage | IslandMessage>(
  message: T,
  nonce?: string | null,
): Envelope<T> => ({
  ...message,
  channel: ISLAND_CHANNEL,
  version: ISLAND_VERSION,
  ...(nonce ? { nonce } : {}),
});

// ---- reading what arrives ------------------------------------------------------

type Raw = Record<string, unknown>;

const text = (value: unknown, max = 512) =>
  typeof value === "string" && value.length > 0 && value.length <= max
    ? value
    : null;
const flag = (value: unknown) =>
  typeof value === "boolean" ? value : undefined;
const count = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const record = (value: unknown): Raw | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Raw)
    : null;

/** The message's fields, if it is on this channel and version at all, and
 *  carries the nonce when one is expected. */
function onChannel(data: unknown, nonce: string | null): Raw | null {
  let raw = data;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const message = record(raw);
  if (
    !message ||
    message.channel !== ISLAND_CHANNEL ||
    message.version !== ISLAND_VERSION ||
    (nonce !== null && message.nonce !== nonce)
  )
    return null;
  return message;
}

/** A saved card as it arrives: only the fields the chip draws, typed. */
function savedCardsOf(value: unknown): Array<SavedCard> | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.flatMap((item: unknown) => {
    const card = record(item);
    return card &&
      text(card.id, 255) &&
      text(card.brand, 64) &&
      text(card.last4, 4) &&
      typeof card.exp_month === "number" &&
      typeof card.exp_year === "number"
      ? [
          {
            id: card.id as string,
            brand: card.brand as string,
            last4: card.last4 as string,
            exp_month: card.exp_month,
            exp_year: card.exp_year,
          },
        ]
      : [];
  });
}

function stateOf(raw: Raw): Partial<CardIslandState> {
  const state: Partial<CardIslandState> = {};
  const savedCards = savedCardsOf(raw.savedCards);
  if (savedCards) state.savedCards = savedCards;
  if (raw.selected === null || text(raw.selected, 255))
    state.selected = raw.selected as string | null;
  const remember = flag(raw.remember);
  if (remember !== undefined) state.remember = remember;
  const locked = flag(raw.locked);
  if (locked !== undefined) state.locked = locked;
  const disabled = flag(raw.disabled);
  if (disabled !== undefined) state.disabled = disabled;
  return state;
}

const ENGINE_STATUSES: ReadonlyArray<
  NonNullable<BuyerWalletPayment["payment_intent_status"]>
> = ["succeeded", "processing", "requires_action"];

/** The payment the engine prepared, all four fields present or nothing (a
 *  null connected account is present: the payment is on the platform
 *  account); what the engine's own confirmation left, when it made one. */
function paymentOf(value: unknown): BuyerWalletPayment | null {
  const raw = record(value);
  const payment_intent_client_secret = text(
    raw?.payment_intent_client_secret,
    255,
  );
  const connected_account_id = text(raw?.connected_account_id, 255);
  const publishable_key = text(raw?.publishable_key, 255);
  const payment_method_id = text(raw?.payment_method_id, 255);
  if (
    !payment_intent_client_secret ||
    (!connected_account_id && raw?.connected_account_id !== null) ||
    !publishable_key ||
    !payment_method_id
  )
    return null;
  const status = ENGINE_STATUSES.find(
    (known) => known === raw?.payment_intent_status,
  );
  return {
    payment_intent_client_secret,
    connected_account_id,
    publishable_key,
    payment_method_id,
    ...(status ? { payment_intent_status: status } : {}),
  };
}

const REASONS: ReadonlyArray<PaymentReason> = ["payment", "payment_again"];

/**
 * A host's message, as the island page reads it. Anything not on this
 * channel and version, or not the shape it claims, is `null`, and so is
 * one without the page's `nonce` when it was loaded with one.
 * `appearanceOf` reads the init's appearance (the page's own type); what it
 * cannot read is left out.
 */
export function parseHostMessage<Appearance>(
  data: unknown,
  appearanceOf: (raw: unknown) => Appearance | null | undefined,
  nonce: string | null = null,
): HostMessage<Appearance> | null {
  const message = onChannel(data, nonce);
  if (!message) return null;
  const email = text(message.email, 320);
  if (message.type === "init") {
    const setup = record(message.setup);
    const publishableKey = text(setup?.publishableKey, 255);
    const clientSecret = text(setup?.clientSecret, 255);
    if (!publishableKey || !clientSecret) return null;
    const appearance = appearanceOf(message.appearance);
    return {
      type: "init",
      setup: { publishableKey, clientSecret },
      email: email ?? "",
      ...(appearance ? { appearance } : {}),
      ...stateOf(message),
    };
  }
  if (message.type === "update")
    return {
      type: "update",
      ...(email ? { email } : {}),
      ...stateOf(message),
    };
  if (message.type === "submit")
    return { type: "submit", ...(email ? { email } : {}) };
  if (message.type === "confirm") {
    const id = text(message.id, 128);
    const payment = paymentOf(message.payment);
    const reason = REASONS.find((one) => one === message.reason);
    if (!id || !payment || !reason) return null;
    return { type: "confirm", id, payment, reason };
  }
  return null;
}

function cardResultOf(value: unknown): CardResult | null {
  const raw = record(value);
  if (!raw) return null;
  const error = text(raw.error, 1024);
  if (error) return { error };
  const paymentMethodId = text(raw.paymentMethodId, 255);
  if (!paymentMethodId || typeof raw.challenged !== "boolean") return null;
  const card = record(raw.card);
  const brand = text(card?.brand, 64);
  const last4 = text(card?.last4, 4);
  return {
    paymentMethodId,
    card: brand && last4 ? { brand, last4 } : null,
    challenged: raw.challenged,
  };
}

const STATUSES: ReadonlyArray<PaymentStatus> = [
  "succeeded",
  "processing",
  "requires_capture",
];

function paymentResultOf(value: unknown): PaymentResult | null {
  const raw = record(value);
  if (!raw) return null;
  const error = text(raw.error, 1024);
  if (error) return { error };
  const paymentIntentId = text(raw.paymentIntentId, 255);
  const status = STATUSES.find((one) => one === raw.status);
  return paymentIntentId && status ? { paymentIntentId, status } : null;
}

/**
 * The island's message, as a host reads it (the app's WebView bridge).
 * Anything not on this channel and version, not carrying the `nonce` the
 * host loaded the page with, or not the shape it claims, is `null`.
 */
export function parseIslandMessage(
  data: unknown,
  nonce: string,
): IslandMessage | null {
  const message = onChannel(data, nonce);
  if (!message) return null;
  switch (message.type) {
    case "ready":
      return { type: "ready" };
    case "bank": {
      const open = flag(message.open);
      return open === undefined ? null : { type: "bank", open };
    }
    case "height": {
      const height = count(message.height);
      return height !== null && height > 0 ? { type: "height", height } : null;
    }
    case "change": {
      const complete = flag(message.complete);
      const empty = flag(message.empty);
      if (complete === undefined || empty === undefined) return null;
      return {
        type: "change",
        complete,
        empty,
        brand: text(message.brand, 64),
      };
    }
    case "focus": {
      const top = count(message.top);
      const bottom = count(message.bottom);
      return top !== null && bottom !== null
        ? { type: "focus", top, bottom }
        : null;
    }
    case "choose": {
      const remember = flag(message.remember);
      if (remember === undefined) return null;
      const selected =
        message.selected === null ? null : text(message.selected, 255);
      if (selected === null && message.selected !== null) return null;
      return { type: "choose", selected, remember };
    }
    case "result": {
      const result = cardResultOf(message.result);
      return result ? { type: "result", result } : null;
    }
    case "confirmed": {
      const id = text(message.id, 128);
      const result = paymentResultOf(message.result);
      return id && result ? { type: "confirmed", id, result } : null;
    }
    default:
      return null;
  }
}
